import { useUser } from "../../context/userContext";
import React, { useCallback, useMemo, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useDot } from "../../context/automatonContext";
import { useAlert } from "../../context/Feedback/alertContext";
import { Tikz2Dot } from "../Dropzone/Tikz2Dot";
import { getExistingMachineNames, generateUniqueName } from "./utils/Support";
import { saveMachine } from "../../services/saveService";
import DOT from "../../components/MachineElement/DOT";

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  borderWidth: 2,
  borderRadius: 2,
  borderColor: "#eeeeee",
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  outline: "none",
  transition: "border .24s ease-in-out",
};

const activeStyle = {
  borderColor: "#2196f3",
};

export default function MyDropzone(props) {
  const {
    setDot,
    setCreatedDate,
    setDotName,
    setMachineStates,
    setMachineTransitions,
    machineStates,
    machineTransitions,
    dot,
  } = useDot();
  const { setAlert } = useAlert();
  const labelAlert = {
    en: "Invalid format",
    pt: "Formato não suportado",
  };

  const { language, credential, user, setUser } = useUser();
  const [readyToSave, setReadyToSave] = useState(false);

  useEffect(() => {
    if (readyToSave && dot) {
      const save = async () => {
        try {
          const dotString = toDotLanguage();
          await saveMachine({
            dot: dotString,
            dotName: dotNameRef.current,
            createdDate: createdDateRef.current,
            credential,
            user,
            setUser,
            setAlert,
            setCreatedDate,
            language,
            isAutoSave: true,
          });
        } catch (error) {
          setAlert({
            active: true,
            text: "Failed to save machine",
            color: "warning",
            severity: "error",
          });
          console.error("Save error:", error);
        } finally {
          setReadyToSave(false);
        }
      };
      save();
    }
  }, [readyToSave, dot]);

  const dotNameRef = React.useRef(null);
  const createdDateRef = React.useRef(null);

  const onDropAccepted = useCallback(
    async ([file]) => {
      const reader = new FileReader();
      setDot("");
      setDotName(null);
      setMachineStates([]);
      setMachineTransitions([]);

      reader.readAsText(file);
      reader.onload = async function () {
        const contents = reader.result;
        const newCreatedDate = new Date().toISOString();

        const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
        const existingNames = getExistingMachineNames(user);
        const uniqueFileName = generateUniqueName(
          fileNameWithoutExtension,
          existingNames
        );

        // Atualiza as refs
        dotNameRef.current = uniqueFileName;
        createdDateRef.current = newCreatedDate;

        setDotName(uniqueFileName);
        setCreatedDate(newCreatedDate);

        if (contents.includes("begin")) {
          const novo = Tikz2Dot(contents);
          setDot(novo);
          props.setOpen(false);
        } else {
          setDot(contents);
          props.setOpen(false);
        }

        setReadyToSave(true);
      };
    },
    [user]
  );

  const toDotLanguage = () => {
    if (dot) {
      return dot;
    }

    let dott = new DOT(machineStates, machineTransitions);
    dott.toDotLanguage();
    return dott.toString();
  };

  const onDropRejected = useCallback(() => {
    setAlert({
      active: true,
      text: labelAlert[language],
      color: "warning",
      sevarity: "error",
    });
  }, [language, setAlert]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    acceptedFiles,
  } = useDropzone({
    onDropRejected,
    accept: [".dot", ".tikz", ".tex"],
    onDropAccepted,
  });

  const acceptedFileItems = acceptedFiles.map((file) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
    </li>
  ));

  const style = useMemo(() => ({
    ...baseStyle,
    ...(isDragActive ? activeStyle : {}),
  }));

  return (
    <div {...getRootProps({ style })}>
      <input {...getInputProps()} />
      <p>{props.text}</p>
      <h4>{acceptedFileItems}</h4>
    </div>
  );
}
